import React from "react";
import useFrontPageCta from "../../../Hooks/useFrontPageCta";
import { Link } from "react-router-dom";

// Components
import ButtonPill from "../../Buttons/ButtonPill";
import ButtonPillGradient from "../../Buttons/ButtonPillGradient";

// Constants
import { RoutePaths } from "../../../Constants/RoutePaths";

// Helpers
import { formatMoney } from "../../../Helpers/Currency/Currency";
import { getOfferPriceQualifier, getOfferRenewalText, getResolvedOffer, hasSpecialOfferStyling, isPrepaidOffer } from "../../../Helpers/BillingOffers";
import { withPromotionCode } from "../../../Helpers/PromotionCode";
import {
    AnnualPlanSavings,
    MarketingPlanPricing,
    resolveAnnualPlanSavings,
    resolveMarketingPlanPricing,
    selectAnnualMarketingPlan,
    selectMonthlyMarketingPlan,
} from "../../../Helpers/PlanPricing";

// Models
import PlanDTO from "../../../Models/DTOs/Plan";
import { PlanPeriod } from "../../../Models/Enums/PlanPeriod";

interface FrontPricingSectionProps {
    plans: PlanDTO[] | null;
    plansLoading: boolean;
    plansError: string;
}

const getSignupPath = (plan: PlanDTO): string => {
    const planSlug = encodeURIComponent(
        plan.title.trim().replace(/\s+/g, "-")
    );
    return withPromotionCode(`${RoutePaths.SignUp[0]}?plan=${planSlug}`);
};

const getPeriodCopy = (plan: PlanDTO) => {
    const isAnnual = plan.period === PlanPeriod.Yearly;

    return {
        cadence: isAnnual ? "Annual" : "Monthly",
        unit: isAnnual ? "year" : "month",
        renewal: isAnnual ? "Renews annually" : "Renews monthly",
        action: isAnnual ? "Choose annual" : "Choose monthly",
    };
};

const getOfferCaption = (
    plan: PlanDTO,
    pricing: MarketingPlanPricing
): string | null => {
    const { unit } = getPeriodCopy(plan);
    const offer = getResolvedOffer(plan);
    if (offer) {
        const qualifier = getOfferPriceQualifier(offer);
        if (isPrepaidOffer(offer)) return `once ${qualifier || "for your introductory period"}`;
        if (offer.duration === "repeating") return `per ${offer.cadence} ${qualifier || ""}`.trim();
        return offer.duration === "forever" ? `per ${offer.cadence}` : qualifier;
    }
    const hasDiscount = pricing.initialPrice !== pricing.renewalPrice;

    if (!hasDiscount || pricing.source === "base") return `per ${unit}`;
    if (plan.period === PlanPeriod.Yearly) return null;
    if (pricing.source === "promotion") return "limited-time promotional price";

    if (
        plan.period === PlanPeriod.Monthly &&
        pricing.discountDurationMonths === 1
    ) {
        return "for your first month";
    }

    if (
        plan.period === PlanPeriod.Monthly &&
        pricing.discountDurationMonths
    ) {
        return `per month for your first ${pricing.discountDurationMonths} months`;
    }

    return "introductory price";
};

const hasDeviceCountClaim = (feature: string): boolean =>
    /\b(?:(?:up to|maximum of)\s+)?(?:\d+|one|two|three|four|five|six|single|unlimited)\s+devices?\b/i.test(
        feature
    );

const getAnnualSavingsFeature = (
    savings: AnnualPlanSavings | null
): string | null => {
    if (!savings) return null;

    const firstYearSavings =
        savings.firstYearSavings === null
            ? null
            : formatMoney(savings.firstYearSavings, savings.currency);
    const renewalYearSavings =
        savings.renewalYearSavings === null
            ? null
            : formatMoney(savings.renewalYearSavings, savings.currency);

    if (
        firstYearSavings &&
        renewalYearSavings &&
        savings.firstYearSavings !== savings.renewalYearSavings
    ) {
        return `Save ${firstYearSavings} in year one, then ${renewalYearSavings} each year versus monthly`;
    }

    if (firstYearSavings && renewalYearSavings) {
        return `Save ${renewalYearSavings} every year versus monthly`;
    }

    if (firstYearSavings) {
        return `Save ${firstYearSavings} in your first year versus monthly`;
    }

    return renewalYearSavings
        ? `Save ${renewalYearSavings} a year versus monthly at standard prices`
        : null;
};

interface PlanCardProps {
    plan: PlanDTO;
    annualSavings: AnnualPlanSavings | null;
}

const PlanCard: React.FC<PlanCardProps> = ({ plan, annualSavings }) => {
    const cta = useFrontPageCta(getResolvedOffer(plan)?.cta_label || getPeriodCopy(plan).action, getSignupPath(plan));
    const pricing = resolveMarketingPlanPricing(plan);
    if (!pricing) return null;

    const periodCopy = getPeriodCopy(plan);
    const offer = getResolvedOffer(plan);
    const specialOffer = hasSpecialOfferStyling(offer);
    const title = offer?.headline || plan.title;
    const initialPrice = formatMoney(
        pricing.initialPrice,
        pricing.currency
    );
    const renewalPrice = formatMoney(
        pricing.renewalPrice,
        pricing.currency
    );
    const hasDiscount = pricing.initialPrice !== pricing.renewalPrice;
    const isAnnual = plan.period === PlanPeriod.Yearly;
    const planFeatures = Array.isArray(plan.features) ? plan.features : [];
    const hasBestValueFeature = planFeatures.some((feature) =>
        /^best value$/i.test(feature.trim())
    );
    const visibleFeatures = planFeatures.filter(
        (feature) =>
            feature.trim() &&
            !/^best value$/i.test(feature.trim()) &&
            !hasDeviceCountClaim(feature)
    );
    const annualSavingsFeature = isAnnual
        ? getAnnualSavingsFeature(annualSavings)
        : null;
    const offerBadge = specialOffer
        ? offer?.badge || "Special offer"
        : !offer && pricing.source === "promotion" && plan.promotionTitle?.trim()
            ? plan.promotionTitle.trim()
            : hasBestValueFeature
                ? "Best value"
                : null;
    const offerCaption = getOfferCaption(plan, pricing);

    return (
        <article
            className={`front-pricing__card${
                isAnnual ? " front-pricing__card--featured" : ""
            }${specialOffer ? ` membership-sale membership-sale--${isAnnual ? "annual" : "monthly"}` : ""}`}
        >
            {offerBadge ? (
                <p className="front-pricing__badge">{offerBadge}</p>
            ) : null}

            <div className="front-pricing__plan-heading">
                <p>{periodCopy.cadence}</p>
                <h3>{title}</h3>
            </div>

            <div className="front-pricing__price">
                <strong>{initialPrice}</strong>
                {offerCaption && <span>{offerCaption}</span>}
            </div>

            <div className="front-pricing__renewal">
                <strong>
                    {offer
                        ? getOfferRenewalText(offer) || `${initialPrice}/${offer.cadence}`
                        : hasDiscount
                        ? pricing.source === "promotion"
                            ? `Standard price ${renewalPrice}/${periodCopy.unit}`
                            : `Then ${renewalPrice}/${periodCopy.unit}`
                        : `${renewalPrice}/${periodCopy.unit}`}
                </strong>
                <span>
                    {!offer && pricing.source === "promotion" && hasDiscount
                        ? "Offer and renewal terms are confirmed during sign-up"
                        : periodCopy.renewal}
                </span>
            </div>

            {offer?.description && <p className="front-pricing__offer-description">{offer.description}</p>}

            {visibleFeatures.length || annualSavingsFeature ? (
                <ul aria-label={`${plan.title} membership features`}>
                    {visibleFeatures.map((feature) => (
                        <li key={feature}>
                            <span aria-hidden="true">&#10003;</span>
                            {feature}
                        </li>
                    ))}
                    {annualSavingsFeature ? (
                        <li
                            className="front-pricing__saving"
                            key="annual-savings"
                        >
                            <span aria-hidden="true">&#10003;</span>
                            <strong>{annualSavingsFeature}</strong>
                        </li>
                    ) : null}
                </ul>
            ) : null}

            <div className="front-pricing__action">
                {isAnnual || specialOffer ? (
                    <ButtonPillGradient
                        text={cta.text}
                        link={cta.link}
                        background={specialOffer ? "var(--offer-accent)" : "linear-gradient(225deg, rgba(187, 54, 184, 1) 0%, #b760b4 100%)"}
                        backgroundHover={specialOffer ? "var(--offer-surface)" : "#b760b4"}
                        color={specialOffer ? "var(--offer-ink)" : "white"}
                    />
                ) : (
                    <ButtonPill
                        label={cta.text}
                        link={cta.link}
                    />
                )}
            </div>
        </article>
    );
};

const FrontPricingSection: React.FC<FrontPricingSectionProps> = ({
    plans,
    plansLoading,
    plansError,
}) => {
    const cta = useFrontPageCta("Continue to membership", RoutePaths.SignUp[0]);
    const monthlyPlan = selectMonthlyMarketingPlan(plans);
    const annualPlan = selectAnnualMarketingPlan(plans);
    const selectedPlans = [
        monthlyPlan,
        annualPlan,
    ].filter((plan): plan is PlanDTO => plan !== null);
    const annualSavings = resolveAnnualPlanSavings(monthlyPlan, annualPlan);
    const validPlans = selectedPlans.filter(
        (plan) => resolveMarketingPlanPricing(plan) !== null
    );
    const isLoading = plansLoading || (plans === null && !plansError);

    return (
        <section
            id="pricing"
            className="front-pricing"
            aria-labelledby="front-pricing-heading"
            aria-busy={isLoading}
            tabIndex={-1}
        >
            <div className="front-pricing__inner">
                <div className="front-pricing__heading">
                    <p className="front-pricing__kicker">Simple membership</p>
                    <h2 id="front-pricing-heading">
                        One membership.
                        <span>A wider view.</span>
                    </h2>
                    <p>
                        Choose the plan that fits the way you watch. Both
                        memberships unlock Ickonic&apos;s independent films,
                        series and current affairs, plus the members&apos; forum&mdash;a
                        place to keep asking, exchange perspectives and get to
                        know the people behind familiar names.
                    </p>
                </div>

                {isLoading ? (
                    <div className="front-pricing__loading" role="status">
                        <span className="front-pricing__status-copy">
                            Loading membership options&hellip;
                        </span>
                        <span aria-hidden="true" />
                        <span aria-hidden="true" />
                    </div>
                ) : validPlans.length ? (
                    <div className="front-pricing__grid">
                        {validPlans.map((plan) => (
                            <PlanCard
                                key={plan.key}
                                plan={plan}
                                annualSavings={annualSavings}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="front-pricing__unavailable" role="status">
                        <p>
                            Membership options are temporarily unavailable.
                        </p>
                        <Link to={cta.link}>
                            {cta.text}
                            <span aria-hidden="true">&rarr;</span>
                        </Link>
                    </div>
                )}

                <p className="front-pricing__fine-print">
                    Introductory offers are subject to eligibility. Memberships
                    renew automatically according to the displayed offer terms
                    unless cancelled. Promotional terms are confirmed during sign-up.{" "}
                    <Link to={RoutePaths.TermsOfUse}>Terms apply.</Link>
                </p>
            </div>
        </section>
    );
};

export default FrontPricingSection;
